import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Sparkle, Plant as PlantIcon, ArrowRight, Bell, ChartLineUp, Mountains, Cherries, Flower, Grains, FilePdf, Translate } from "@phosphor-icons/react";
import { toast } from "sonner";

const FALLBACK_PLANS = [
  {
    id: "free",
    name: "Field",
    price_usd: 0,
    period: "forever",
    scan_limit_monthly: 5,
    plant_limit: 1,
    alerts: false,
    features: [
      "AI plant diagnosis (GPT-5.5 vision)",
      "Live climate context",
      "Scan history & PDF export",
    ],
  },
  {
    id: "pro_monthly",
    name: "Grower",
    price_usd: 4.99,
    period: "30 days",
    scan_limit_monthly: null,
    plant_limit: 30,
    alerts: true,
    features: [
      "Unlimited plant scans",
      "Soil scans — texture, moisture, pH hints & amendments",
      "Fruit scans — ripeness, grade, shelf-life & defects",
      "Flower scans — bloom stage, pollination & care tips",
      "Seed scans — viability, germination % & sow timing",
      "Up to 30 plants in your collection",
      "Daily climate-stress alerts at your location",
      "8-language localized diagnoses",
      "Branded PDF reports for every scan",
      "Priority GPT-5.5 diagnoses",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        if (Array.isArray(data?.plans) && data.plans.length > 0) {
          setPlans(data.plans);
        }
      } catch {
        // keep FALLBACK_PLANS so the page is never empty
      }
      if (user && user !== false) {
        try {
          const { data } = await api.get("/billing/me");
          setBilling(data);
        } catch {}
      }
    })();
  }, [user]);

  const upgrade = async () => {
    if (!user || user === false) {
      window.location.href = "/login";
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="pricing-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-data text-mute">pricing</div>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-tight">
            Start free. Grow with <em className="italic text-terra">your climate</em>.
          </h1>
          <p className="text-mute mt-4 leading-relaxed">
            A Must For Earth is free for anyone curious about their plants. Upgrade to Grower
            when you're ready for climate-stress alerts and an unlimited field journal.
          </p>
          {billing?.is_pro && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest/10 text-forest border border-forest/30">
              <CheckCircle size={16} weight="fill" /> Pro active until{" "}
              <span className="font-mono-data">{new Date(billing.pro_until).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {plans.map((p) => {
            const isPro = p.id === "pro_monthly";
            return (
              <div
                key={p.id}
                className={`relative rounded-3xl p-8 border ${isPro ? "bg-ink text-white border-ink" : "bg-sand-100 border-sand-200 text-ink"}`}
                data-testid={`plan-${p.id}`}
              >
                {isPro && (
                  <span className="absolute -top-3 right-6 tag !bg-ochre !text-ink !border-ochre-dark">recommended</span>
                )}
                <div className={`font-mono-data ${isPro ? "text-white/70" : "text-mute"}`}>{p.id === "free" ? "free forever" : "billed every 30 days"}</div>
                <h2 className={`font-display text-4xl mt-2 ${isPro ? "text-ochre" : "text-ink"}`}>{p.name}</h2>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-6xl leading-none">${p.price_usd}</span>
                  <span className={`font-mono-data ${isPro ? "text-white/70" : "text-mute"}`}>{p.id === "free" ? "/ ever" : "/ 30 days"}</span>
                </div>

                {isPro && (
                  <div
                    className="mt-6 rounded-2xl border border-ochre/40 bg-ochre/10 p-4"
                    data-testid="pro-new-features-ribbon"
                  >
                    <div className="font-mono-data text-ochre uppercase text-[10px] tracking-widest">new this season</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20">
                        <Mountains size={12} weight="duotone" /> Soil scan
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20">
                        <Cherries size={12} weight="duotone" /> Fruit scan
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20">
                        <Flower size={12} weight="duotone" /> Flower scan
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20">
                        <Grains size={12} weight="duotone" /> Seed scan
                      </span>
                    </div>
                  </div>
                )}

                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => {
                    const lf = f.toLowerCase();
                    let FeatureIcon = CheckCircle;
                    if (lf.startsWith("soil scan")) FeatureIcon = Mountains;
                    else if (lf.startsWith("fruit scan")) FeatureIcon = Cherries;
                    else if (lf.startsWith("flower scan")) FeatureIcon = Flower;
                    else if (lf.startsWith("seed scan")) FeatureIcon = Grains;
                    else if (lf.includes("alerts")) FeatureIcon = Bell;
                    else if (lf.includes("pdf")) FeatureIcon = FilePdf;
                    else if (lf.includes("language")) FeatureIcon = Translate;
                    else if (lf.includes("plants in")) FeatureIcon = PlantIcon;
                    else if (lf.includes("priority") || lf.includes("unlimited")) FeatureIcon = Sparkle;
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <FeatureIcon size={18} weight="duotone" className={isPro ? "text-ochre" : "text-forest"} />
                        <span className={isPro ? "text-white/90" : "text-ink/90"}>{f}</span>
                      </li>
                    );
                  })}
                  {p.scan_limit_monthly && (
                    <li className={`flex items-start gap-2 ${isPro ? "text-white/70" : "text-mute"}`}>
                      <ChartLineUp size={16} weight="duotone" /> {p.scan_limit_monthly} scans / month
                    </li>
                  )}
                  {p.plant_limit && (
                    <li className={`flex items-start gap-2 ${isPro ? "text-white/70" : "text-mute"}`}>
                      <PlantIcon size={16} weight="duotone" /> Up to {p.plant_limit} {p.plant_limit === 1 ? "plant" : "plants"}
                    </li>
                  )}
                </ul>

                <div className="mt-8">
                  {isPro ? (
                    billing?.is_pro ? (
                      <button disabled className="btn-primary w-full justify-center !bg-forest/30 !text-white/70" data-testid="plan-pro-active">
                        Already Pro
                      </button>
                    ) : (
                      <button
                        onClick={upgrade}
                        disabled={busy}
                        className="btn-primary w-full justify-center !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white"
                        data-testid="upgrade-cta"
                      >
                        <Sparkle size={16} weight="fill" /> {busy ? "Opening Stripe…" : "Upgrade to Grower"}
                      </button>
                    )
                  ) : (
                    <Link to={user ? "/scan" : "/register"} className="btn-secondary w-full justify-center" data-testid="plan-free-cta">
                      {user ? "Continue scanning" : "Start free"} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {[
            { Icon: Mountains, title: "Soil, Fruit & Flower scans", body: "Specialist GPT-5.5 analyzers go beyond leaves — diagnose soil quality, fruit ripeness & defects, and floral bloom stage with climate context." },
            { Icon: Bell, title: "Climate-stress alerts", body: "We watch your local weather and ping you when temperature, humidity, UV or rain crosses each plant's threshold." },
            { Icon: FilePdf, title: "Branded PDF reports", body: "Export any scan as a polished report — perfect for greenhouse crews, agronomists, or your own field journal." },
          ].map((f) => (
            <div key={f.title} className="bg-sand-100 border border-sand-200 rounded-2xl p-5">
              <f.Icon size={24} weight="duotone" className="text-forest" />
              <h3 className="font-display text-2xl mt-2">{f.title}</h3>
              <p className="text-mute mt-1 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-mute text-xs mt-10 font-mono-data inline-flex items-center justify-center gap-2 w-full" data-testid="pricing-secure">
          <span className="w-2 h-2 rounded-full bg-forest" /> Secure payments by Stripe · cancel anytime
        </p>
      </main>
    </div>
  );
}
